import { Button } from '@material-ui/core';
import { batch, useDispatch, useSelector } from 'react-redux';
import { findVariantByPrice } from '../../utils/getCurrentVariant';
import allActions from '../../actions/product_builder';
import { unwrapResult } from '@reduxjs/toolkit';
import { v4 } from 'uuid';
import submitImageToStorage from '../../utils/submitImageToStorage';
import getSnapshotOfState from '../../utils/getSnapshotOfState';
import {
    createVariantForCIEM,
    getVariantsForCIEM
} from '../../reducers/shopify_api/ShopifyCIEMSlice';
import { uploadStateToCloud } from '../../reducers/store_state/StoreStateSlice';
import buttons from '../../styles/buttons.module.css';

import { useTranslation } from 'react-i18next';
import { getURLForCustomArtwork } from '../../utils/getArtworkUrl';
import { submitOrder } from '../../reducers/shopify_api/ShopifyCIEMSlice';
import { generateOrderInfo } from '../../utils/generateOrderInfo';
import { useContext } from 'react';
import { PreviewDataContext } from '../../context/PreviewContext';

const AddToCart = ({ setOpenAddToCartBackdrop }) => {
    const { t } = useTranslation();
    const currState = useSelector((state) => state.rootReducer);
    const currentCiem = useSelector((state) => state.shopifyReducer.currentCiem);
    const { preview } = useContext(PreviewDataContext);
    const dispatch = useDispatch();
    const artworkState = useSelector((state) => state.rootReducer.currentArtwork);

    const resolveVariant = async () => {
        const productGid = `gid://shopify/Product/${currentCiem.id}`;
        // Always refetch so prices beyond Shopify's old 100-variant page are included
        // (A18s 4198 variants live past the first 100).
        const variantData = await dispatch(getVariantsForCIEM(productGid)).then(unwrapResult);
        let variant = findVariantByPrice(variantData.variants, currState.iemPrice.total);

        if (variant) {
            return variant;
        }

        try {
            variant = await dispatch(
                createVariantForCIEM({
                    id: currentCiem.id,
                    price: currState.iemPrice.total,
                    sku: currentCiem.sku
                })
            ).then(unwrapResult);
            return variant;
        } catch (error) {
            // create fails on products with >100 variants via REST — refetch and
            // use an existing price match if generate previously created one.
            const refreshed = await dispatch(getVariantsForCIEM(productGid)).then(unwrapResult);
            variant = findVariantByPrice(refreshed.variants, currState.iemPrice.total);
            if (variant) {
                return variant;
            }
            throw error;
        }
    };

    const handleSubmitOrder = async () => {
        setOpenAddToCartBackdrop(true);
        try {
            let storedImagesObject = {};
            const folderName = v4();
            storedImagesObject = await getURLForCustomArtwork(artworkState, folderName);
            storedImagesObject['preview'] = await submitImageToStorage(
                folderName,
                preview,
                'preview'
            );
            batch(() => {
                dispatch(allActions.artworkActions.setURLToCustomArtwork(storedImagesObject));
                dispatch(allActions.previewActions.setPreview(storedImagesObject.preview));
            });

            const stateSnapshot = JSON.stringify(getSnapshotOfState(currState, storedImagesObject));
            const uploadResult = await dispatch(uploadStateToCloud(stateSnapshot)).then(
                unwrapResult
            );
            const configID = uploadResult.id;
            const variant = await resolveVariant();

            if (!variant?.id) {
                throw new Error('Could not resolve Shopify variant for this configuration price');
            }

            const orderInfo = generateOrderInfo(
                currentCiem,
                variant,
                currState,
                storedImagesObject,
                configID
            );
            await dispatch(submitOrder(orderInfo)).then(unwrapResult);
            window.open(window.location.origin + '/cart', '_self');
        } catch (error) {
            console.error('Add to cart failed:', error);
            setOpenAddToCartBackdrop(false);
            alert(
                'Unable to add this configuration to cart. Please try again or contact support if the problem continues.'
            );
        }
    };

    return (
        <Button
            disableRipple={true}
            className={buttons.btnPill + ' ' + buttons.btnAddCart}
            onClick={() => handleSubmitOrder()}>
            {t('summary.add_to_cart')}
        </Button>
    );
};

export default AddToCart;
